import {
  Award,
  Download,
  Edit,
  FileText,
  Gift,
  Plus,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { recompenseService } from "../services/api";

const Recompenses = () => {
  const [recompenses, setRecompenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecompense, setEditingRecompense] = useState(null);
  const [formData, setFormData] = useState({
    titre: "",
    description: "",
    type_recompense: "",
    condition_points: 0,
    condition_tampons: null,
    contenu: "",
  });

  useEffect(() => {
    fetchRecompenses();
  }, []);

  const fetchRecompenses = async () => {
    try {
      setLoading(true);
      const response = await recompenseService.getRecompenses();
      setRecompenses(response.data);
    } catch (error) {
      console.error("Error fetching rewards:", error);
      toast.error("Erreur lors du chargement des récompenses");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRecompense) {
        await recompenseService.updateRecompense(
          editingRecompense.id,
          formData
        );
        toast.success("Récompense mise à jour avec succès");
      } else {
        await recompenseService.createRecompense(formData);
        toast.success("Récompense créée avec succès");
      }

      setShowModal(false);
      setEditingRecompense(null);
      resetForm();
      fetchRecompenses();
    } catch (error) {
      console.error("Error saving reward:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (recompense) => {
    setEditingRecompense(recompense);
    setFormData({
      titre: recompense.titre,
      description: recompense.description || "",
      type_recompense: recompense.type_recompense,
      condition_points: recompense.condition_points,
      condition_tampons: recompense.condition_tampons || "",
      contenu: recompense.contenu || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (recompense) => {
    if (
      window.confirm("Êtes-vous sûr de vouloir supprimer cette récompense ?")
    ) {
      try {
        await recompenseService.deleteRecompense(recompense.id);
        toast.success("Récompense supprimée avec succès");
        fetchRecompenses();
      } catch (error) {
        console.error("Error deleting reward:", error);
        toast.error("Erreur lors de la suppression");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      titre: "",
      description: "",
      type_recompense: "",
      condition_points: 0,
      condition_tampons: null,
      contenu: "",
    });
  };

  const openModal = () => {
    setEditingRecompense(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRecompense(null);
    resetForm();
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "badge":
        return <Award className="h-5 w-5" />;
      case "certificat":
        return <FileText className="h-5 w-5" />;
      case "reduction":
        return <Gift className="h-5 w-5" />;
      default:
        return <Award className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "badge":
        return "bg-yellow-100 text-yellow-800";
      case "certificat":
        return "bg-blue-100 text-blue-800";
      case "reduction":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Gestion des récompenses
            </h1>
            <p className="text-gray-600">
              Créez et gérez les récompenses pour motiver les parents
            </p>
          </div>
          <button
            onClick={openModal}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Nouvelle récompense</span>
          </button>
        </div>
      </div>

      {/* Récompenses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {recompenses.map((recompense) => (
          <div key={recompense.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div
                  className={`p-2 rounded-lg ${getTypeColor(
                    recompense.type_recompense
                  )}`}
                >
                  {getTypeIcon(recompense.type_recompense)}
                </div>
                <div className="ml-3">
                  <h3 className="font-semibold text-gray-900">
                    {recompense.titre}
                  </h3>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(
                      recompense.type_recompense
                    )}`}
                  >
                    {recompense.type_recompense}
                  </span>
                </div>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => handleEdit(recompense)}
                  className="text-primary-600 hover:text-primary-900 p-1"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(recompense)}
                  className="text-red-600 hover:text-red-900 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {recompense.description && (
              <p className="text-sm text-gray-600 mb-4">
                {recompense.description}
              </p>
            )}

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Condition points:</span>
                <span className="font-medium">
                  {recompense.condition_points}
                </span>
              </div>
              {recompense.condition_tampons && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Condition tampons:</span>
                  <span className="font-medium">
                    {recompense.condition_tampons}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  recompense.actif
                    ? "bg-success-100 text-success-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {recompense.actif ? "Actif" : "Inactif"}
              </span>

              {recompense.type_recompense === "certificat" && (
                <button className="btn-secondary text-xs flex items-center space-x-1">
                  <Download className="h-3 w-3" />
                  <span>Prévisualiser</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingRecompense
                ? "Modifier la récompense"
                : "Nouvelle récompense"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Titre *</label>
                  <input
                    type="text"
                    value={formData.titre}
                    onChange={(e) =>
                      setFormData({ ...formData, titre: e.target.value })
                    }
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="label">Type de récompense *</label>
                  <select
                    value={formData.type_recompense}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        type_recompense: e.target.value,
                      })
                    }
                    className="input-field"
                    required
                  >
                    <option value="">Sélectionner un type</option>
                    <option value="badge">Badge</option>
                    <option value="certificat">Certificat</option>
                    <option value="reduction">Réduction</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="input-field"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Condition points *</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.condition_points}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        condition_points: parseInt(e.target.value),
                      })
                    }
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="label">Condition tampons</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.condition_tampons || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        condition_tampons: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              {formData.type_recompense === "certificat" && (
                <div>
                  <label className="label">Contenu du certificat</label>
                  <textarea
                    value={formData.contenu}
                    onChange={(e) =>
                      setFormData({ ...formData, contenu: e.target.value })
                    }
                    className="input-field"
                    rows={4}
                    placeholder="Texte qui apparaîtra sur le certificat..."
                  />
                </div>
              )}

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingRecompense ? "Mettre à jour" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recompenses;
